import { useContext, useEffect, useRef, useState } from "react"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { SwipeCards } from "../../components/meet/SwipeCards"
import { SwipeCardSkeleton } from "../../components/meet/SwipeCardSkeleton"
import { getAccounts, getNearbyAccounts } from "../../controllers/accounts/accounts"
import { UserContext } from "../../context/UserContext"
import { Box, Button, Typography } from "@mui/material"
import { AccessTime, WavingHand } from "@mui/icons-material"
import { createMatch, getMatches } from "../../controllers/hey_match/match"
import { useProfileComplete } from "../../hooks/useProfileComplete"
import { DownloadAppPopup } from "../../components/DownloadAppPopup"
import { getDistanceFilter, setDistanceFilter } from "../../util/distanceFilter"

const MeetHeader = () => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={900} sx={{
            background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.2,
        }}>
            Meet People
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Wave hey, send a super hey, or skip to the next person.
        </Typography>
    </Box>
)

const EmptyState = ({ icon, title, body, action }) => (
    <Box sx={{
        maxWidth: 400, mx: 'auto', mt: 8, textAlign: 'center',
        p: 4, borderRadius: 3,
        background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
        border: '1px solid rgba(115,41,130,0.15)',
    }}>
        <Box sx={{ fontSize: 48, mb: 1.5 }}>{icon}</Box>
        <Typography fontWeight={800} fontSize={18} gutterBottom>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: action ? 2.5 : 0 }}>{body}</Typography>
        {action}
    </Box>
)

export const Meet = () => {
    const [offset, setOffset] = useState(0)
    const [local, setLocal] = useState(true)
    const [miles, setMiles] = useState(10)
    const [lat, setLat] = useState('')
    const [lng, setLng] = useState('')

    // Restore the last-used distance filter (set via the Meet settings dialog).
    const distanceFilterLoaded = useRef(false)
    useEffect(() => {
        const saved = getDistanceFilter()
        setMiles(saved.miles)
        setLat(saved.lat)
        setLng(saved.lng)
        distanceFilterLoaded.current = true
    }, [])

    useEffect(() => {
        if (!distanceFilterLoaded.current) return
        setDistanceFilter({ miles, lat, lng })
    }, [miles, lat, lng])

    const [errImg, setErrImg] = useState([])
    const [account, setAccount] = useState(null)
    const [filterAccounts, setFilteredAccounts] = useState([])
    const { userDetails } = useContext(UserContext)
    const [swipeIdx, setSwipeIdx] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initializingMatch, setInitializingMatch] = useState(false)
    const [accounts, setAccounts] = useState([])
    const [matches, setMatches] = useState(null)
    const hasAttemptedCreateRef = useRef(false)

    useProfileComplete(userDetails)

    const token = userDetails?.token ?? ''
    const id = userDetails?.id

    const usingLocationFilter = !!(lat && lng)

    const allAccts = async () => {
        if (!token || !id) { setLoading(false); return }
        setLoading(true)
        try {
            const local_accounts = usingLocationFilter
                ? await getNearbyAccounts({ token, mastodon_id: id, latitude: lat, longitude: lng, miles, skip: offset })
                : await getAccounts(offset, local, token)
            setAccounts(local_accounts)
            const local_matches = await getMatches({ token, id })
            setMatches(local_matches)
            if (!account && local_accounts?.length > 0 && local_matches?.heymatch && Array.isArray(local_matches.heymatch.viewed)) {
                const nextAccount = local_accounts.find(item => !local_matches.heymatch.viewed.includes(item.id))
                setAccount(nextAccount ?? local_accounts[0])
            }
        } catch (error) {
            console.error('Error fetching accounts/matches:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (token && id) allAccts()
        else setLoading(false)
    }, [id, offset, local, token, lat, lng, miles])

    useEffect(() => {
        setOffset(0)
        setSwipeIdx(0)
    }, [local, lat, lng, miles])

    useEffect(() => {
        const availableAccounts = accounts.filter(item => item.id !== id)
        setFilteredAccounts(availableAccounts)
        if (!account && availableAccounts.length > 0 && matches?.heymatch && Array.isArray(matches.heymatch.viewed)) {
            const nextAccount = availableAccounts.find(item => !matches.heymatch.viewed.includes(item.id))
            setAccount(nextAccount ?? availableAccounts[0])
        }
        if (accounts.length > 0 && matches !== null) setLoading(false)
    }, [id, accounts, matches, account])

    useEffect(() => {
        if (!token || !id || loading) return
        if (matches?.heymatch && Array.isArray(matches?.heymatch?.viewed)) return
        if (hasAttemptedCreateRef.current) return
        hasAttemptedCreateRef.current = true
        setInitializingMatch(true)
        createMatch({ id, viewed: [], token })
            .then(() => allAccts())
            .catch(() => { hasAttemptedCreateRef.current = false })
            .finally(() => setInitializingMatch(false))
    }, [token, id, loading, matches])

    // Loading / initializing
    if (!userDetails || Object.keys(userDetails).length === 0 ||
        (loading && token && id && filterAccounts.length === 0) ||
        initializingMatch || (!matches?.heymatch || !Array.isArray(matches?.heymatch?.viewed))) {
        return (
            <MainLoggedInLayout>
                <MeetHeader />
                <SwipeCardSkeleton />
            </MainLoggedInLayout>
        )
    }

    // Not logged in
    if (!userDetails?.id) {
        return (
            <MainLoggedInLayout>
                <MeetHeader />
                <EmptyState
                    icon="👋"
                    title="Meet new people"
                    body="Log in to start meeting queer folks from around the world."
                />
            </MainLoggedInLayout>
        )
    }

    // Rate limited
    if (matches?.resultMessage?.en === "Too many request.") {
        return (
            <MainLoggedInLayout>
                <DownloadAppPopup />
                <MeetHeader />
                <EmptyState
                    icon={<AccessTime sx={{ fontSize: 48, color: '#FF8C00' }} />}
                    title="Swipe limit reached"
                    body="You've reached your swipe limit. Come back in a few hours when it resets."
                />
            </MainLoggedInLayout>
        )
    }

    // No results
    if (filterAccounts?.length === 0) {
        return (
            <MainLoggedInLayout>
                <DownloadAppPopup />
                <MeetHeader />
                <EmptyState
                    icon={<WavingHand sx={{ fontSize: 48, color: '#732982' }} />}
                    title="You've seen everyone!"
                    body={local
                        ? "You've gone through all local members. Expand your search to meet people from connected servers."
                        : "No more profiles to show right now. Check back soon!"}
                    action={local && (
                        <Button
                            variant="contained"
                            onClick={() => setLocal(false)}
                            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#732982', '&:hover': { background: '#5c1f68' } }}
                        >
                            Search all servers
                        </Button>
                    )}
                />
            </MainLoggedInLayout>
        )
    }

    return (
        <MainLoggedInLayout>
            <DownloadAppPopup />
            <MeetHeader />
            <SwipeCards
                setMatches={setMatches}
                local={local}
                setLocal={setLocal}
                usingLocationFilter={usingLocationFilter}
                miles={miles}
                setMiles={setMiles}
                setLat={setLat}
                setLng={setLng}
                setAccount={setAccount}
                filterAccounts={filterAccounts}
                accts_viewed={matches?.heymatch?.viewed}
                count={accounts?.length}
                offset={offset}
                setOffset={setOffset}
                swipeIdx={swipeIdx}
                setSwipeIdx={setSwipeIdx}
                account={account}
                errImg={errImg}
                setErrImg={setErrImg}
            />
        </MainLoggedInLayout>
    )
}
